import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Lock, CheckCircle, Plus, X, TestTube } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { AdminLabTests } from '@/components/AdminLabTests';

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('billing'); // 'billing' or 'tests'

  // Form state
  const [formData, setFormData] = useState({
    patientName: '',
    mobile: '',
    age: '',
    gender: 'Male',
    date: new Date().toISOString().split('T')[0],
    totalAmount: '',
    paidAmount: '',
    dueAmount: '',
    paymentMode: 'Cash'
  });

  const [tests, setTests] = useState([{ name: '', price: '' }]);

  const ADMIN_PASSWORD = 'Ushakiran@2026';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Admin access granted!');
    } else {
      toast.error('Incorrect password. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-calculate due amount
    if (name === 'totalAmount' || name === 'paidAmount') {
      const total = name === 'totalAmount' ? parseFloat(value) || 0 : parseFloat(formData.totalAmount) || 0;
      const paid = name === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(formData.paidAmount) || 0;
      setFormData(prev => ({ ...prev, dueAmount: (total - paid).toFixed(2) }));
    }
  };

  const handleTestChange = (index, field, value) => {
    const updatedTests = [...tests];
    updatedTests[index][field] = value;
    setTests(updatedTests);

    // Auto-calculate total from test prices
    if (field === 'price') {
      const total = updatedTests.reduce((sum, test) => sum + (parseFloat(test.price) || 0), 0);
      setFormData(prev => ({ 
        ...prev, 
        totalAmount: total.toFixed(2),
        dueAmount: (total - (parseFloat(prev.paidAmount) || 0)).toFixed(2)
      }));
    }
  };

  const addTest = () => {
    setTests([...tests, { name: '', price: '' }]);
  };

  const removeTest = (index) => {
    if (tests.length > 1) {
      const updatedTests = tests.filter((_, i) => i !== index);
      setTests(updatedTests);
      
      // Recalculate total
      const total = updatedTests.reduce((sum, test) => sum + (parseFloat(test.price) || 0), 0);
      setFormData(prev => ({ 
        ...prev, 
        totalAmount: total.toFixed(2),
        dueAmount: (total - (parseFloat(prev.paidAmount) || 0)).toFixed(2)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate mobile number
      if (!/^\d{10}$/.test(formData.mobile)) {
        toast.error('Please enter a valid 10-digit mobile number');
        setLoading(false);
        return;
      }

      // Filter out empty tests
      const validTests = tests.filter(test => test.name.trim() && test.price);
      
      if (validTests.length === 0) {
        toast.error('Please add at least one test');
        setLoading(false);
        return;
      }

      // Prepare data for Firestore
      const billData = {
        patientName: formData.patientName.trim(),
        mobile: formData.mobile,
        age: parseInt(formData.age),
        gender: formData.gender,
        date: formData.date,
        tests: validTests.map(test => ({
          name: test.name.trim(),
          price: parseFloat(test.price)
        })),
        totalAmount: parseFloat(formData.totalAmount),
        paidAmount: parseFloat(formData.paidAmount),
        dueAmount: parseFloat(formData.dueAmount),
        paymentMode: formData.paymentMode,
        paymentStatus: parseFloat(formData.dueAmount) === 0 ? 'Paid' : 'Partial',
        createdAt: serverTimestamp(),
        invoiceId: `USH${Date.now()}`
      };

      // Add to Firestore
      await addDoc(collection(db, 'patient_bills'), billData);

      toast.success('Patient bill created successfully!');

      // Reset form
      setFormData({
        patientName: '',
        mobile: '',
        age: '',
        gender: 'Male',
        date: new Date().toISOString().split('T')[0],
        totalAmount: '',
        paidAmount: '',
        dueAmount: '',
        paymentMode: 'Cash'
      });
      setTests([{ name: '', price: '' }]);

    } catch (error) {
      console.error('Error adding bill:', error);
      toast.error('Failed to create bill. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50/30 flex items-center justify-center px-4 py-12">
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md p-8 shadow-xl border-slate-200">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-[#0284C7] to-[#059669] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Admin Access</h1>
            <p className="text-sm text-slate-600">Enter password to access patient billing</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="mt-2 text-lg py-6"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-[#0284C7] to-[#059669] text-white py-6 text-base font-semibold hover:shadow-lg transition-all duration-300"
            >
              <Lock className="w-5 h-5 mr-2" />
              Login
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // Admin Dashboard with Tabs
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50/30 px-4 py-8 sm:py-12">
      <Toaster position="top-center" richColors />
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <Badge className="bg-gradient-to-r from-[#0284C7] to-[#059669] text-white mb-4">
            <CheckCircle className="w-4 h-4 mr-1" />
            Admin Panel
          </Badge>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
            Patient Billing & Lab Management
          </h1>
          <p className="text-sm sm:text-base text-slate-600">Create invoices and manage lab tests</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('billing')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'billing'
                ? 'text-rose-600 border-b-2 border-rose-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Patient Billing
          </button>
          <button
            onClick={() => setActiveTab('tests')}
            className={`px-6 py-3 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'tests'
                ? 'text-rose-600 border-b-2 border-rose-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <TestTube className="w-4 h-4" />
            Lab Tests CRUD
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'billing' ? (
        <Card className="p-6 sm:p-8 shadow-xl border-slate-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Patient Information */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900 border-b pb-2">Patient Information</h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="patientName" className="text-slate-700 font-medium">
                    Patient Name *
                  </Label>
                  <Input
                    id="patientName"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="mobile" className="text-slate-700 font-medium">
                    Mobile Number *
                  </Label>
                  <Input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData(prev => ({ ...prev, mobile: value }));
                    }}
                    placeholder="10-digit mobile"
                    maxLength={10}
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="age" className="text-slate-700 font-medium">
                    Age *
                  </Label>
                  <Input
                    id="age"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder="Enter age"
                    min="1"
                    max="150"
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="gender" className="text-slate-700 font-medium">
                    Gender *
                  </Label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="mt-2 flex h-9 w-full rounded-md border border-slate-300 bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0284C7]"
                    required
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="date" className="text-slate-700 font-medium">
                    Date *
                  </Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="mt-2"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Tests Conducted */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h2 className="text-xl font-semibold text-slate-900">Tests Conducted</h2>
                <Button
                  type="button"
                  onClick={addTest}
                  variant="outline"
                  size="sm"
                  className="border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Test
                </Button>
              </div>

              {tests.map((test, index) => (
                <div key={index} className="flex gap-2 sm:gap-4 items-end">
                  <div className="flex-1">
                    <Label className="text-slate-700 font-medium">Test Name *</Label>
                    <Input
                      value={test.name}
                      onChange={(e) => handleTestChange(index, 'name', e.target.value)}
                      placeholder="e.g., CBC, Lipid Profile"
                      className="mt-2"
                      required
                    />
                  </div>
                  <div className="w-32 sm:w-40">
                    <Label className="text-slate-700 font-medium">Price (₹) *</Label>
                    <Input
                      type="number"
                      value={test.price}
                      onChange={(e) => handleTestChange(index, 'price', e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className="mt-2"
                      required
                    />
                  </div>
                  {tests.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removeTest(index)}
                      variant="outline"
                      size="icon"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Payment Details */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900 border-b pb-2">Payment Details</h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="totalAmount" className="text-slate-700 font-medium">
                    Total Amount (₹) *
                  </Label>
                  <Input
                    id="totalAmount"
                    name="totalAmount"
                    type="number"
                    value={formData.totalAmount}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="mt-2 bg-slate-50"
                    readOnly
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="paidAmount" className="text-slate-700 font-medium">
                    Paid Amount (₹) *
                  </Label>
                  <Input
                    id="paidAmount"
                    name="paidAmount"
                    type="number"
                    value={formData.paidAmount}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="dueAmount" className="text-slate-700 font-medium">
                    Due Amount (₹)
                  </Label>
                  <Input
                    id="dueAmount"
                    name="dueAmount"
                    type="number"
                    value={formData.dueAmount}
                    className="mt-2 bg-slate-50"
                    readOnly
                  />
                </div>

                <div>
                  <Label htmlFor="paymentMode" className="text-slate-700 font-medium">
                    Payment Mode *
                  </Label>
                  <select
                    id="paymentMode"
                    name="paymentMode"
                    value={formData.paymentMode}
                    onChange={handleInputChange}
                    className="mt-2 flex h-9 w-full rounded-md border border-slate-300 bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0284C7]"
                    required
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="Paytm">Paytm</option>
                    <option value="Online Transfer">Online Transfer</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#0284C7] to-[#059669] text-white py-6 text-base font-semibold hover:shadow-xl transition-all duration-300"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Creating Bill...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Create Patient Bill
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
        ) : (
          <AdminLabTests />
        )}
      </div>
    </div>
  );
};